import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight, ArrowUpRight, BarChart3, Banknote, Boxes, Factory, GraduationCap,
  MapPin, Menu, ShieldCheck, Smartphone, Store, Users, Users2, Wrench, X,
} from 'lucide-react';
import { ThemeToggle } from '../../shell/ThemeToggle';
import { useCms, cmsText } from '../../api/cms';
import { PlansPreview } from './PlansPreview';
import { Reveal } from './Reveal';

/**
 * ITCity company landing — itcitysoft.com. ITCity is the PARENT company (a
 * software house in Sylhet); Possignin is its main product (the POS). This page
 * speaks as the parent brand — neutral "IT" mark, products showcase — and is
 * deliberately distinct from the Possignin product landing at /possignin
 * (possignin.com). POS-forward in the body, because Possignin is the flagship
 * and, today, the only shipping product.
 */

function ITCityMark({ className = '' }: { className?: string }) {
  return (
    <Link to="/home" className={`group inline-flex items-center gap-2.5 rounded-lg ${className}`} aria-label="ITCity home">
      <span
        className="w-8 h-8 rounded-lg bg-zinc-950 dark:bg-white grid place-items-center text-white dark:text-zinc-950 font-bold text-[13px] tracking-tight leading-none transition-transform duration-200 group-hover:scale-[1.04]"
        aria-hidden="true"
      >
        IT
      </span>
      <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">ITCity</span>
    </Link>
  );
}

const FEATURES = [
  { icon: Boxes, title: 'Sales & inventory', body: 'Fast sell screen, FIFO and lot tracking, barcodes, and low-stock alerts across locations.' },
  { icon: Banknote, title: 'BDT-native billing', body: 'SSLCommerz built in, cash-to-office in Sylhet, auto invoices and receipts — all in taka.' },
  { icon: Users, title: 'HRM & payroll', body: 'Staff, shifts, attendance, leave, and payroll — the people side of your shop, handled.' },
  { icon: Store, title: 'CRM & contacts', body: 'Customers, suppliers, dues, and follow-ups. Know who owes what at a glance.' },
  { icon: Factory, title: 'Manufacturing', body: 'Recipes, ingredients, and production runs for bakeries, kitchens, and workshops.' },
  { icon: Wrench, title: 'Repair & service', body: 'Job sheets, device history, and service status for repair shops and workshops.' },
  { icon: BarChart3, title: 'Reports that read', body: 'Daily sales, P&L, stock, and tax — plain reports you can act on. Export any time.' },
  { icon: Smartphone, title: 'Runs on a phone', body: 'Android, iPhone, or any browser. Receipt printers and barcode scanners supported.' },
  { icon: ShieldCheck, title: 'Calm & secure', body: '2FA, audit logs, role-based access. Your data lives in your own database — never sold.' },
];

const PRODUCTS: {
  icon: typeof Store;
  name: string;
  tag: string;
  status: 'live' | 'soon';
  body: string;
  to?: '/possignin';
}[] = [
  { icon: Store, name: 'Possignin', tag: 'Cloud POS', status: 'live', body: 'Point of sale and full business management. Our flagship — running 100+ shops today.', to: '/possignin' },
  { icon: GraduationCap, name: 'ITCity School', tag: 'School management', status: 'soon', body: 'Admissions, attendance, fees, and results — the same calm software, for education.' },
  { icon: Users2, name: 'ITCity CRM', tag: 'Sales & relationships', status: 'soon', body: 'Leads, pipelines, and follow-ups for service businesses built on relationships.' },
];

const NAV = [
  { label: 'Products', href: '#products' },
  { label: 'What it does', href: '#features' },
  { label: 'About', href: '#about' },
] as const;

export function Company() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { data: cms } = useCms();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark text-zinc-900 dark:text-zinc-100">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-zinc-950 focus:text-white focus:text-sm focus:font-medium focus:shadow-lg dark:focus:bg-white dark:focus:text-zinc-950"
      >
        Skip to content
      </a>

      {/* Header — ITCity (parent brand) */}
      <header
        className={
          'sticky top-0 z-40 border-b transition-colors duration-200 ' +
          (scrolled
            ? 'border-zinc-200/80 dark:border-zinc-800/80 bg-white/85 dark:bg-zinc-950/85 backdrop-blur-md supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-zinc-950/70'
            : 'border-transparent bg-transparent')
        }
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <ITCityMark />
          <nav className="hidden md:flex items-center gap-7 text-sm" aria-label="Primary">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors">
                {n.label}
              </a>
            ))}
            <Link to="/blog" className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors">Blog</Link>
            <Link to="/become-a-reseller" className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors">Partners</Link>
          </nav>
          <div className="hidden md:flex items-center gap-2.5">
            <Link to="/login" className="text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50 px-2 py-1 rounded-md transition-colors">Sign in</Link>
            <Link to="/signup" className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand-hover shadow-xs transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover">
              Start free <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <ThemeToggle />
          </div>
          <button
            type="button"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden w-11 h-11 -mr-2 grid place-items-center rounded-lg text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
                {n.label}
              </a>
            ))}
            <Link to="/blog" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">Blog</Link>
            <Link to="/become-a-reseller" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">Partners</Link>
            <div className="flex items-center justify-between px-1 pt-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Theme</span>
              <ThemeToggle />
            </div>
            <div className="pt-2 flex gap-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100">Sign in</Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-brand dark:bg-brand-dm dark:text-zinc-950">Start free</Link>
            </div>
          </div>
        )}
      </header>

      <main id="main">
        {/* Hero — POS-forward, ITCity-framed */}
        <section className="relative overflow-hidden border-b border-zinc-200/70 dark:border-zinc-800/70">
          <div className="glow-bg absolute inset-x-0 top-0 h-[34rem] -z-10 pointer-events-none" aria-hidden="true" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 text-center">
            <Reveal>
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 text-xs font-medium text-zinc-600 dark:text-zinc-300 shadow-xs">
                <MapPin className="w-3.5 h-3.5 text-brand dark:text-brand-dm" aria-hidden="true" />
                {cmsText(cms, 'company.hero', 'eyebrow', 'Software house · Sylhet, Bangladesh')}
              </p>
            </Reveal>
            <Reveal delay={60}>
              <h1 className="mt-5 text-[2.5rem] leading-[1.04] sm:text-6xl lg:text-[3.75rem] font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-3xl mx-auto text-balance">
                {cmsText(cms, 'company.hero', 'headline', 'Local software, built to last.')}
              </h1>
            </Reveal>
            <Reveal delay={120}>
              {cms?.['company.hero']?.subhead ? (
                <p className="mt-5 text-lg sm:text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed text-pretty">
                  {cms['company.hero'].subhead}
                </p>
              ) : (
                <p className="mt-5 text-lg sm:text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed text-pretty">
                  ITCity builds <span className="font-medium text-zinc-900 dark:text-zinc-100">Possignin</span> — cloud point of
                  sale with inventory, HRM, CRM, manufacturing, and repair. One calm system, priced in taka, running 100+ shops
                  across Bangladesh.
                </p>
              )}
            </Reveal>
            <Reveal delay={180}>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/signup" className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-white bg-brand hover:bg-brand-hover shadow-sm transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover">
                  Start your free trial <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
                <Link to="/possignin" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition-colors">
                  Explore Possignin
                </Link>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <ul className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-sm text-zinc-600 dark:text-zinc-300">
                {['7-day free trial', 'No card required', 'Setup in 5 minutes', 'Bangla + English'].map((t) => (
                  <li key={t} className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        {/* POS features */}
        <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">What Possignin does</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">One system instead of six.</h2>
            <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed text-pretty">
              Most shops juggle a register, a spreadsheet, a notebook, and a chat group. Possignin replaces all of it.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-200/70 dark:bg-zinc-800/70">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 60} className="group bg-white dark:bg-zinc-950 p-6 transition-colors hover:bg-zinc-50/80 dark:hover:bg-zinc-900/60">
                <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm transition-transform duration-200 group-hover:scale-105">
                  <f.icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{f.title}</h3>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{f.body}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/features" className="group inline-flex items-center gap-1.5 text-sm font-medium text-brand dark:text-brand-dm">
              See all features <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </section>

        {/* Products — ITCity is more than one product (Possignin is the flagship today) */}
        <section id="products" className="border-y border-zinc-200/70 dark:border-zinc-800/70 bg-white/50 dark:bg-zinc-900/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <Reveal className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">ITCity products</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">Possignin today. More on the way.</h2>
            </Reveal>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {PRODUCTS.map((p, i) => {
                const inner = (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="w-11 h-11 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm transition-transform duration-200 group-hover:scale-105">
                        <p.icon className="w-6 h-6" aria-hidden="true" />
                      </span>
                      {p.status === 'live' ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> Live</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-800">Coming soon</span>
                      )}
                    </div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">{p.name}</h3>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{p.tag}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{p.body}</p>
                    {p.status === 'live' && (
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand dark:text-brand-dm">Visit Possignin <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                    )}
                  </>
                );
                const cls = 'rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-6 flex flex-col h-full';
                return p.to ? (
                  <Reveal key={p.name} delay={i * 70} className="h-full">
                    <Link to={p.to} className={`${cls} group hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors`}>{inner}</Link>
                  </Reveal>
                ) : (
                  <Reveal key={p.name} delay={i * 70} className="h-full">
                    <div className={`${cls} group`}>{inner}</div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Live plans (real published catalog) */}
        <PlansPreview />

        {/* About ITCity */}
        <section id="about" className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">About ITCity</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight text-balance">Built in Sylhet, for businesses here.</h2>
            <div className="mt-5 space-y-4 text-zinc-600 dark:text-zinc-300 leading-relaxed text-pretty">
              <p>
                ITCity is a team of engineers and designers in Sylhet who believe local businesses deserve software that
                fits them — priced in taka, fluent in Bangla, and patient enough for a busy counter.
              </p>
              <p>
                We started with Possignin because the shops around us were running on notebooks and memory. Today it powers
                over a hundred businesses, directly and through resellers like Ogrogoti and Nexico. We build slowly and keep
                what we ship excellent.
              </p>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="group inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand-hover transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover">
                Start free <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link to="/become-a-reseller" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors">
                Become a reseller
              </Link>
              <a href="mailto:hello@itcitysoft.com" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors">
                Contact ITCity
              </a>
            </div>
          </Reveal>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex flex-col items-center sm:items-start gap-2">
            <ITCityMark />
            <p className="text-xs text-zinc-500 dark:text-zinc-400">Software house · Sylhet, Bangladesh</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-zinc-500 dark:text-zinc-400">
            <Link to="/possignin" className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Possignin</Link>
            <Link to="/become-a-reseller" className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Become a reseller</Link>
            <Link to="/blog" className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Blog</Link>
            <Link to="/terms" className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Privacy</Link>
            <a href="mailto:hello@itcitysoft.com" className="hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">Contact</a>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">© 2026 ITCity</p>
        </div>
      </footer>
    </div>
  );
}
